import type z from 'zod/v4'
import { relations } from 'drizzle-orm'
import { index, pgTable, primaryKey, text, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { user } from '../../../auth'
import { Challenge } from './challenge'

/**
 * Challenge to User relationship table
 * Stores individual user ids invited to a challenge that is not public, on top of (a union with)
 * the school organizations in `challenge_to_organization` and the classes in `challenge_to_team`.
 * A challenge with no row in any of the three is public.
 */
export const ChallengeToUser = pgTable(
  'challenge_to_user',
  {
    challengeId: uuid()
      .notNull()
      .references(() => Challenge.id, { onDelete: 'cascade' }),
    userId: text()
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
  },
  (table) => [
    primaryKey({ columns: [table.challengeId, table.userId] }),
    index('challenge_to_user_challenge_idx').on(table.challengeId),
    index('challenge_to_user_user_idx').on(table.userId),
  ],
)

export const challengeToUserRelations = relations(ChallengeToUser, ({ one }) => ({
  challenge: one(Challenge, {
    fields: [ChallengeToUser.challengeId],
    references: [Challenge.id],
  }),
  user: one(user, {
    fields: [ChallengeToUser.userId],
    references: [user.id],
  }),
}))

export const InsertChallengeToUserSchema = createInsertSchema(ChallengeToUser)
export const UpdateChallengeToUserSchema = createUpdateSchema(ChallengeToUser).partial()
export const SelectChallengeToUserSchema = createSelectSchema(ChallengeToUser)

export type InsertChallengeToUser = typeof ChallengeToUser.$inferInsert
export type UpdateChallengeToUser = z.infer<typeof UpdateChallengeToUserSchema>
export type SelectChallengeToUser = typeof ChallengeToUser.$inferSelect
